import React, { useEffect, useRef, useState } from "react"
import * as Select from "@radix-ui/react-select"

import { ChevronDown } from "components/SVG"
import type { Option } from "../Option"
import { DropdownOption } from "../Option"
import * as styles from "./Trigger.css"

type TriggerProps = {
  ariaLabel: string
  options: Option[]
  preselectedOption?: Option
  variant?: "default" | "error" | "success"
  onChange: (value: string) => void
}

const getKeyFromValue = (options: Option[], value: string) =>
  options.find((option) => option.value === value)?.label ?? ""

const areAllValuesPresent = (obj: Option) =>
  Object.values(obj).every((value) => value)

export const DropdownTrigger = ({
  ariaLabel,
  onChange,
  options,
  preselectedOption,
  variant = "default",
}: TriggerProps): JSX.Element => {
  const initialOption =
    preselectedOption && areAllValuesPresent(preselectedOption)
      ? preselectedOption
      : options[0]

  const [selectedOption, setSelectedOption] = useState(initialOption)
  const [isFlipped, setIsFlipped] = useState(false)
  const dropdownTriggerRef = useRef<HTMLButtonElement>(null)

  const hasOptionChanged = selectedOption.value !== initialOption.value
  const isValid = variant === "success"
  const isNotValid = variant === "error"

  useEffect(() => {
    const handleScroll = () => {
      if (dropdownTriggerRef.current) {
        const { top, bottom } =
          dropdownTriggerRef.current.getBoundingClientRect()
        const viewportHeight = window.innerHeight
        const componentMiddle = (top + bottom) / 2

        if (componentMiddle < viewportHeight / 2) {
          setIsFlipped(false)
        } else {
          setIsFlipped(true)
        }
      }
    }

    window.addEventListener("scroll", handleScroll)
    handleScroll()

    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [])

  useEffect(() => {
    if (selectedOption.value === initialOption.value) return
    onChange(selectedOption.value)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption])

  return (
    <Select.Root
      onValueChange={(value) =>
        setSelectedOption({ label: getKeyFromValue(options, value), value })
      }
      value={selectedOption.value}
    >
      <Select.Trigger
        className={`
          ${styles.trigger.default}
          ${isValid ? styles.trigger.success : styles.trigger.default}
          ${isNotValid ? styles.trigger.error : styles.trigger.default}
          ${hasOptionChanged ? styles.trigger.active : ""}
          `}
        data-testid="dropdown-trigger"
        aria-label={ariaLabel}
        ref={dropdownTriggerRef}
      >
        <Select.Value placeholder={selectedOption.label} />
        <Select.Icon className={styles.triggerIcon}>
          <ChevronDown />
        </Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Content
          className={styles.optionsContainer}
          position="popper"
          ref={(ref) => {
            // This is to prevent elements behind the dropdown from being clicked
            ref?.addEventListener("touchend", (e) => {
              e.preventDefault()
            })
          }}
          side={isFlipped ? "top" : "bottom"}
          sideOffset={8}
        >
          <Select.Viewport className={styles.optionsScrollableContainer}>
            {options.map((option) => (
              <DropdownOption
                key={option.value}
                label={option.label}
                value={option.value}
              />
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  )
}
